import React, { useState, useEffect, useCallback } from 'react';
import './HeroCardPreviewShell.css';
import './NaptarHeroCardPreview.css';
import {
  MONTH_NAMES,
  DAY_HEADERS_SHORT,
  EVENT_TYPES,
  getDaysInMonth,
  formatTime,
  calculateEndTime,
  flattenEventsByDate,
  mergeEventsByDate,
  eventsByDayForMonth,
  getUpcomingEvents,
  isToday,
  dayAbbrevForDate,
  fetchMonthEvents,
} from '../utils/calendarHelpers';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const NaptarHeroCardPreview = ({ onNavigate }) => {
  const [viewDate, setViewDate] = useState(() => new Date());
  const [eventsByDate, setEventsByDate] = useState({});
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const year = viewDate.getFullYear();
      const month = viewDate.getMonth() + 1;
      const nextMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
      const nextYear = nextMonthDate.getFullYear();
      const nextMonth = nextMonthDate.getMonth() + 1;

      const [current, next] = await Promise.all([
        fetchMonthEvents(API_BASE, year, month),
        fetchMonthEvents(API_BASE, nextYear, nextMonth),
      ]);

      setEventsByDate(mergeEventsByDate(current, next));
    } catch {
      setEventsByDate({});
    } finally {
      setLoading(false);
    }
  }, [viewDate]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const daysByNumber = eventsByDayForMonth(eventsByDate, viewDate);
  const flatEvents = flattenEventsByDate(eventsByDate);
  const upcoming = getUpcomingEvents(flatEvents, 3);
  const gridDays = getDaysInMonth(viewDate);

  const navigateMonth = (direction, e) => {
    e.stopPropagation();
    setViewDate(
      new Date(viewDate.getFullYear(), viewDate.getMonth() + direction, 1)
    );
  };

  const handleCta = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate();
  };

  const handlePanelAction = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate();
  };

  return (
    <div
      className="hero-card-shell naptar-hero-preview"
      role="presentation"
      onClick={onNavigate}
    >
      <div className="hero-card-shell__header">
        <span className="hero-card-shell__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M4 9h16M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
        <div>
          <h3 className="hero-card-shell__title">Naptár</h3>
          <p className="hero-card-shell__subtitle">
            Tekintse meg havi programjainkat és közelgő eseményeinket egy helyen. Napi
            foglalkozások, kulturális programok és közösségi élmények várnak Önre.
          </p>
        </div>
      </div>

      <div className="hero-card-shell__panel naptar-hero-preview__panel">
        <div className="naptar-hero-preview__columns">
          <div className="naptar-hero-preview__calendar-col">
            <div className="naptar-hero-preview__month-bar">
              <button
                type="button"
                className="naptar-hero-preview__nav-btn"
                onClick={(e) => navigateMonth(-1, e)}
                aria-label="Előző hónap"
              >
                ‹
              </button>
              <span className="naptar-hero-preview__month-label">
                {MONTH_NAMES[viewDate.getMonth()]} {viewDate.getFullYear()}
              </span>
              <button
                type="button"
                className="naptar-hero-preview__nav-btn"
                onClick={(e) => navigateMonth(1, e)}
                aria-label="Következő hónap"
              >
                ›
              </button>
            </div>

            <div className="naptar-hero-preview__grid-wrap">
              <div className="naptar-hero-preview__weekdays">
                {DAY_HEADERS_SHORT.map((label, i) => (
                  <span key={`${label}-${i}`} className="naptar-hero-preview__weekday">
                    {label}
                  </span>
                ))}
              </div>
              <div className="naptar-hero-preview__grid">
                {gridDays.map((day, index) => {
                  const hasEvents = Boolean(day && daysByNumber[day]?.length);
                  const today = isToday(day, viewDate);
                  return (
                    <div
                      key={`${day ?? 'e'}-${index}`}
                      className={`naptar-hero-preview__cell${day ? '' : ' is-empty'}${hasEvents ? ' has-events' : ''}${today ? ' is-today' : ''}`}
                    >
                      {day ? (
                        <>
                          <span className="naptar-hero-preview__day-num">{day}</span>
                          {hasEvents && <span className="naptar-hero-preview__dot" />}
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              className="naptar-hero-preview__col-btn"
              onClick={handlePanelAction}
            >
              <span className="naptar-hero-preview__col-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M4 9h16M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              Ugrás a hónaphoz
            </button>
          </div>

          <div className="naptar-hero-preview__events-col">
            <div className="naptar-hero-preview__upcoming-head">
              <span className="naptar-hero-preview__bell" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 4a5 5 0 0 0-5 5v3.5l-1.5 2.5h13L17 12.5V9a5 5 0 0 0-5-5z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                  <path d="M10 18a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <span>Közelgő események</span>
            </div>

            {loading ? (
              <p className="naptar-hero-preview__muted">Betöltés…</p>
            ) : upcoming.length === 0 ? (
              <p className="naptar-hero-preview__muted">Nincs közelgő esemény</p>
            ) : (
              <ul className="naptar-hero-preview__event-list">
                {upcoming.map((event) => {
                  const typeMeta = EVENT_TYPES[event.type] || EVENT_TYPES.cultural;
                  const endTime = calculateEndTime(event.time, event.duration || 60);
                  const dayNum = parseInt(event.date.split('-')[2], 10);
                  return (
                    <li key={event.id || `${event.date}-${event.time}-${event.title}`}>
                      <div className="naptar-hero-preview__event-date">
                        <span className="naptar-hero-preview__event-day">{dayNum}</span>
                        <span className="naptar-hero-preview__event-dow">
                          {dayAbbrevForDate(event.date)}
                        </span>
                      </div>
                      <div className="naptar-hero-preview__event-body">
                        <span className="naptar-hero-preview__event-title">{event.title}</span>
                        <span className="naptar-hero-preview__event-time">
                          {formatTime(event.time)}
                          {endTime ? ` – ${endTime}` : ''}
                        </span>
                      </div>
                      <span
                        className="naptar-hero-preview__event-icon"
                        style={{ color: typeMeta.color }}
                        aria-hidden="true"
                      >
                        {typeMeta.icon}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            <button
              type="button"
              className="naptar-hero-preview__col-btn naptar-hero-preview__col-btn--accent"
              onClick={handlePanelAction}
            >
              Mai programok <span aria-hidden="true">›</span>
            </button>
          </div>
        </div>
      </div>

      <button type="button" className="hero-card-shell__cta" onClick={handleCta}>
        PROGRAMOK MEGTEKINTÉSE <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

export default NaptarHeroCardPreview;
